/**
 * Booking Status Display Component
 * Shows the current status of a booking with details
 * Updated for unified booking status system
 */

import { useState, useEffect } from 'react';
import type { UnifiedBookingStatus } from '@leal-crm/shared';
import { StatusTimeline } from './StatusTimeline';
import { ImageThumbnailGrid } from '../ui/ImageThumbnailGrid';
import { getBookingImages, type BookingImageInfo } from '../../services/publicBookingApi';

interface BookingInfo {
  id: string;
  bookingReference: string;
  status: UnifiedBookingStatus;
  workflowStage?: string; // Legacy, for backwards compat
  statusInfo?: {
    code: string;
    name: string;
    color: string;
    bgColor: string | null;
    borderColor: string | null;
    icon: string | null;
    statusType: string;
  } | null;
  scheduledAt: string;
  confirmed: boolean;
  serviceType?: {
    id: string;
    name: string;
  };
  service?: {
    id: string;
    name: string;
  };
  vehicle: {
    plate: string;
    make?: string;
    model?: string;
  };
  statusHistory: Array<{
    id: string;
    fromStage: string | null;
    toStage: string;
    publicNote: string | null;
    createdAt: string;
    toStatusInfo?: {
      code: string;
      name: string;
      color: string;
      bgColor: string | null;
      borderColor: string | null;
      icon: string | null;
      statusType: string;
    } | null;
  }>;
  timeline?: Array<{
    id: string;
    eventType: string;
    previousStatus: string | null;
    newStatus: string | null;
    publicNote: string | null;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface BookingStatusDisplayProps {
  booking: BookingInfo;
  onBack?: () => void;
  language?: 'es' | 'en';
  plate?: string;
}

export function BookingStatusDisplay({
  booking,
  onBack,
  language = 'es',
  plate,
}: BookingStatusDisplayProps) {
  const t = language === 'es' ? translations.es : translations.en;
  const [images, setImages] = useState<BookingImageInfo[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // Fetch images when booking changes
  useEffect(() => {
    async function fetchImages() {
      if (!booking.id) return;

      setLoadingImages(true);
      try {
        const imgs = await getBookingImages(booking.id, {
          plate: plate || booking.vehicle.plate,
          ref: booking.bookingReference,
        });
        setImages(imgs);
      } catch (err) {
        console.error('Failed to fetch booking images:', err);
      } finally {
        setLoadingImages(false);
      }
    }

    fetchImages();
  }, [booking.id, booking.bookingReference, booking.vehicle.plate, plate]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString(language === 'es' ? 'es-ES' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string, statusInfo?: { name?: string | null }): string => {
    return statusInfo?.name || status;
  };

  // Get the current status - use unified status or fall back to workflowStage
  const currentStatus = booking.status || booking.workflowStage || 'CONFIRMED';
  const currentStatusInfo = booking.statusInfo;

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t.backToSearch}
        </button>
      )}

      {/* Main status card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Reference and status badge */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <p className="text-sm text-gray-500">{t.reference}</p>
            <p className="text-2xl font-mono font-bold text-gray-900">{booking.bookingReference}</p>
          </div>
          <div
            className="mt-2 sm:mt-0 px-4 py-2 rounded-full text-sm font-medium"
            style={{
              color: currentStatusInfo?.color ?? undefined,
              backgroundColor: currentStatusInfo?.bgColor ?? undefined,
              borderColor: currentStatusInfo?.borderColor ?? undefined,
            }}
          >
            {getStatusLabel(currentStatus, currentStatusInfo ?? undefined)}
          </div>
        </div>

        {/* Vehicle info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
            </svg>
            <div>
              <p className="font-mono font-bold text-lg">{booking.vehicle.plate}</p>
              {(booking.vehicle.make || booking.vehicle.model) && (
                <p className="text-gray-500 text-sm">
                  {[booking.vehicle.make, booking.vehicle.model].filter(Boolean).join(' ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Appointment details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-500">{t.scheduledDate}</p>
            <p className="font-medium">{formatDate(booking.scheduledAt)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t.scheduledTime}</p>
            <p className="font-medium">{formatTime(booking.scheduledAt)}</p>
          </div>
        </div>

        {(booking.service || booking.serviceType) && (
          <div className="mb-4">
            <p className="text-sm text-gray-500">{t.serviceType}</p>
            <p className="font-medium">{booking.service?.name || booking.serviceType?.name}</p>
          </div>
        )}

        {/* Confirmation status */}
        <div className="flex items-center">
          {booking.confirmed ? (
            <span className="flex items-center text-green-600">
              <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {t.confirmed}
            </span>
          ) : (
            <span className="flex items-center text-yellow-600">
              <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              {t.pendingConfirmation}
            </span>
          )}
        </div>
      </div>

      {/* Status timeline */}
      {booking.statusHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t.statusHistory}</h3>
          <StatusTimeline entries={booking.statusHistory} language={language} />
        </div>
      )}

      {/* Customer uploaded images */}
      {loadingImages ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t.attachedPhotos}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-square bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      ) : images.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t.attachedPhotos}</h3>
          <ImageThumbnailGrid
            images={images.map((img) => ({
              id: img.id,
              url: img.url,
              originalFilename: img.originalFilename,
              uploadedAt: img.uploadedAt,
            }))}
            columns={3}
            emptyMessage={t.noPhotos}
          />
        </div>
      ) : null}
    </div>
  );
}

const translations = {
  es: {
    backToSearch: 'Volver a buscar',
    reference: 'Referencia',
    scheduledDate: 'Fecha programada',
    scheduledTime: 'Hora',
    serviceType: 'Tipo de servicio',
    confirmed: 'Cita confirmada',
    pendingConfirmation: 'Pendiente de confirmar',
    statusHistory: 'Historial de estado',
    attachedPhotos: 'Fotos adjuntas',
    noPhotos: 'No hay fotos adjuntas',
  },
  en: {
    backToSearch: 'Back to search',
    reference: 'Reference',
    scheduledDate: 'Scheduled date',
    scheduledTime: 'Time',
    serviceType: 'Service type',
    confirmed: 'Booking confirmed',
    pendingConfirmation: 'Pending confirmation',
    statusHistory: 'Status history',
    attachedPhotos: 'Attached photos',
    noPhotos: 'No photos attached',
  },
};

export default BookingStatusDisplay;
